﻿import axios, { eventsAuthenticatedAxios } from './axios'
import type { EventDto } from '@/types'

const path = `events`
const v2Path = `v2/events`
const COPENHAGEN_TIME_ZONE = 'Europe/Copenhagen'

export type EventImageUpdateMode = 'keepHistory' | 'replace'

function getTimeZoneOffsetMinutes(date: Date, timeZone: string) {
    const formatter = new Intl.DateTimeFormat('sv-SE', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
    })

    const parts = formatter.formatToParts(date).reduce<Record<string, string>>((result, part) => {
        if (part.type !== 'literal') {
            result[part.type] = part.value
        }

        return result
    }, {})

    return Math.round(
        (Date.UTC(
            Number(parts.year),
            Number(parts.month) - 1,
            Number(parts.day),
            Number(parts.hour),
            Number(parts.minute),
            Number(parts.second)
        ) - date.getTime()) / 60000
    )
}

function parseCopenhagenDateTime(value?: string | Date) {
    if (!value) {
        return undefined
    }

    if (value instanceof Date) {
        return value
    }

    // Respect timestamps that already declare an explicit offset or UTC.
    if (/([zZ]|[+-]\d{2}:\d{2})$/.test(value)) {
        return new Date(value)
    }

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?$/)

    if (!match) {
        return new Date(value)
    }

    const [, year, month, day, hour = '00', minute = '00', second = '00'] = match
    const naiveUtcTimestamp = Date.UTC(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
    )
    const offsetMinutes = getTimeZoneOffsetMinutes(new Date(naiveUtcTimestamp), COPENHAGEN_TIME_ZONE)

    return new Date(naiveUtcTimestamp - offsetMinutes * 60000)
}

export async function getEventById(id: number) {
    const { data } = await axios.get<EventDto>(`${path}/${id}`)
    return deserializeEvent(data)
}
export async function getAllEvents() {
    const { data } = await axios.get<EventDto[]>(`${path}`)
    return data.map(deserializeEvent)
}
export async function getEventsForCreator(creatorId: string, creatorPartitionKey: string) {
    const { data } = await eventsAuthenticatedAxios.get<EventDto[]>(`${path}/Creator/${creatorPartitionKey}/${creatorId}`)
    return data.map(deserializeEvent)
}
export async function getEventsForUser(userId: string, includeDeleted = false) {
    const { data } = await eventsAuthenticatedAxios.get<EventDto[]>(`${path}/user/${userId}`, {
        params: includeDeleted ? { includeDeleted: true } : undefined
    })
    return data.map(deserializeEvent)
}
export async function createEvent(event: EventDto, image?: File, originalImage?: File, idempotencyKey?: string) {
    const formData = new FormData()
    
    formData.append(
        'event',
        new Blob([JSON.stringify(event)], {
            type: 'application/json'
        })
    )
    if (image) {
        formData.append('image', image)
    }
    if (originalImage) {
        formData.append('originalImage', originalImage)
    }

    const headers: Record<string, string> = { 'Content-Type': 'multipart/form-data' }
    if (idempotencyKey) {
        headers['Idempotency-Key'] = idempotencyKey
        headers['X-Create-Attempt'] = '1'
    }

    for (let attempt = 0; attempt < 5; attempt++) {
        try {
            headers['X-Create-Attempt'] = String(attempt + 1)
            const { data } = await eventsAuthenticatedAxios.post<EventDto>(`${v2Path}`, formData, { headers })
            return deserializeEvent(data)
        } catch (error: any) {
            const inProgress = error?.response?.status === 409 && String(error?.response?.data?.error ?? error?.response?.data?.Error ?? '').includes('in-progress')
            if (!inProgress || attempt === 4) {
                throw error
            }
            await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)))
        }
    }

    throw new Error('Oprettelsen blev ikke bekræftet.')
}

export async function findDuplicateCandidates(event: EventDto) {
    const { data } = await eventsAuthenticatedAxios.post<Array<{ id: number; title?: string; start?: string }>>(`${v2Path}/preflight`, event)
    return data ?? []
}
export async function updateEvent(event: EventDto, image?: File, imageUpdateMode: EventImageUpdateMode = 'keepHistory', originalImage?: File) {
    // Validate event has a valid ID before attempting update
    if (!event.id || event.id <= 0) {
        const error = new Error(`Cannot update event: invalid ID (${event.id}). Event object: ${JSON.stringify(event).substring(0, 200)}`);
        console.error('❌ API:', error.message);
        throw error;
    }
    
    const formData = new FormData()
    formData.append(
        'event',
        new Blob([JSON.stringify(event)], {
            type: 'application/json'
        })
    )
    if (image) {
        formData.append('image', image)
    }
    if (originalImage) {
        formData.append('originalImage', originalImage)
    }
    
    try {
        const { data } = await eventsAuthenticatedAxios.put<EventDto>(`${v2Path}/${event.id}`, formData, {
            params: imageUpdateMode === 'replace' ? { replaceImage: true } : undefined,
            headers: { 'Content-Type': 'multipart/form-data' }
        })
        return deserializeEvent(data)
    } catch (error: any) {
        console.error(`❌ API: Failed to update event ${event.id}:`, error.message);
        if (error.response?.status === 404) {
            console.error(`   Event with ID ${event.id} not found on server. This may be due to a race condition or stale data.`);
        }
        throw error;
    }
}
export async function apiPublishEvent(event: EventDto, userId:string, userEmail:string) {
    if (!event.id || event.id === 0) {
        console.error('❌ CRITICAL: apiPublishEvent called with invalid event ID:', event.id, 'event:', event);
        throw new Error(`Cannot publish event without valid ID. Got: ${event.id}`);
    }
    
    await eventsAuthenticatedAxios.post(`${path}/${event.id}/publish`, event, {
        params: { userId, userEmail }
    })
}

export async function apiUnpublishEvent(event: EventDto) {
    if (!event.id || event.id === 0) {
        console.error('❌ CRITICAL: apiUnpublishEvent called with invalid event ID:', event.id, 'event:', event)
        throw new Error(`Cannot unpublish event without valid ID. Got: ${event.id}`)
    }

    const { data } = await eventsAuthenticatedAxios.post<EventDto>(`${path}/${event.id}/unpublish`, null)
    return deserializeEvent(data)
}

export async function deleteEventById(id: number, partitionKey: string) {
    await eventsAuthenticatedAxios.delete(`${path}/${id}/${partitionKey}`)
}

export async function apiSoftDeleteEvent(id: number, partitionKey: string) {
    await eventsAuthenticatedAxios.post(`${path}/${id}/soft-delete`, null, {
        params: { partitionKey }
    })
}
function deserializeEvent(event: EventDto) {
    event.start = parseCopenhagenDateTime(event.start)
    event.end = parseCopenhagenDateTime(event.end)
    event.publish = parseCopenhagenDateTime(event.publish)
    event.cancelledAt = parseCopenhagenDateTime(event.cancelledAt)
    return event
}
// Event Statistics Types
export interface DailyEventTypeCount {
    typeId: number
    typeName: string
    totalEvents: number
    publishedEvents: number
    unpublishedEvents: number
}

export interface DailyEventCount {
    date: string
    totalEvents: number
    publishedEvents: number
    unpublishedEvents: number
    eventsByType: DailyEventTypeCount[]
}

export interface EventsByType {
    typeId: number
    typeName: string
    totalEvents: number
    publishedEvents: number
    unpublishedEvents: number
}

export interface EventStatisticsDto {
    todayPublished: number
    todayUnpublished: number
    lastWeekTotal: number
    lastWeekPublished: number
    lastWeekUnpublished: number
    dailyBreakdown: DailyEventCount[]
    todayEventsByType: EventsByType[]
    lastWeekEventsByType: EventsByType[]
    periodStart: string
    periodEnd: string
}

export async function getEventStatistics(): Promise<EventStatisticsDto> {
    const { data } = await axios.get<EventStatisticsDto>(`${path}/statistics`)
    return data
}